import { useCallback, useEffect, useState } from 'react';
import { Share } from 'types';
import { getShare } from '../api/getShare';

type UseGetShareProps = {
  shareId?: string;
  fieldsToPopulate?: string[];
  refetchKey?: number;
};

export const useGetShare = ({
  shareId,
  fieldsToPopulate,
  refetchKey,
}: UseGetShareProps) => {
  const [share, setShare] = useState<Share | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const fieldsKey = fieldsToPopulate?.join(',') ?? '';

  const fetchShare = useCallback(
    async (controller: AbortController) => {
      if (!shareId) return;

      try {
        setIsLoading(true);

        const responseShare = (await getShare({
          shareId,
          params: { fieldsToPopulate },
          signal: controller.signal,
        })) as Share;

        if (!controller.signal.aborted) {
          setShare(responseShare);
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shareId, fieldsKey, refetchKey]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchShare(controller);
    return () => controller.abort();
  }, [fetchShare]);

  return {
    data: { share },
    isLoading,
    isError,
  };
};
